import { pageTitle } from '@palaccount/config';
import { Plus } from 'lucide-react';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router';

import { Amount, Ltr } from '@/components/amount';
import { Gated } from '@/components/permission-gate';
import { Pager } from '@/components/pager';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { usePagedState, usePageRange } from '@/lib/paging';
import { useBaseCurrency } from '@/lib/session';

import { InventoryTabs } from './inventory-tabs';
import { ApprovalChip, DirectionChip, VoucherStatusChip } from './stock-chips';
import { useStockAdjustments } from './stock-api';

/**
 * INV-08 §4 — the voucher register. Stock In and Stock Out share one list
 * (the direction is a chip, not a screen), newest first, with the approval
 * verdict shown only where the gate is switched on.
 */
export function AdjustmentsListPage() {
  // GEN-107: what these figures are IN, from the session rather than a
  // literal. Every one of them is a ledger or stock valuation, which the
  // company holds in its base currency; nothing here converts, so GEN-116
  // keeps the disclosure off the screen.
  const baseCurrency = useBaseCurrency();
  const { t } = useTranslation();
  const navigate = useNavigate();
  /** NFR-42 — the page rides in the URL. */
  const paging = usePagedState();
  const { data, isPending, isError } = useStockAdjustments({}, paging.page, paging.pageSize);
  const adjustments = data?.adjustments;
  const range = usePageRange(data, paging.pageSize);

  useEffect(() => {
    document.title = pageTitle(t('adj.title'));
  }, [t]);

  return (
    <div className="grid gap-4">
      <InventoryTabs />
      <div className="flex flex-wrap items-center gap-3">
        <h2 className="me-auto text-lg font-bold">{t('adj.title')}</h2>
        <Gated permission="inventory.create" reasonKey="perm.cannot.createAdjustments">
          <Button asChild>
            <Link to="/inventory/adjustments/new">
              <Plus aria-hidden /> {t('common.new')}
            </Link>
          </Button>
        </Gated>
      </div>

      <Card>
        <CardContent>
          {isPending ? (
            <div className="h-40 animate-pulse rounded-md bg-muted" aria-busy />
          ) : isError ? (
            <p role="alert" className="py-8 text-center text-sm text-destructive">
              {t('common.loadFailed')}
            </p>
          ) : (adjustments ?? []).length === 0 ? (
            <p className="py-12 text-center text-sm text-muted-foreground">{t('adj.empty')}</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('adj.colCode')}</TableHead>
                  <TableHead>{t('adj.colDirection')}</TableHead>
                  <TableHead>{t('common.date')}</TableHead>
                  <TableHead>{t('adj.colWarehouse')}</TableHead>
                  <TableHead>{t('adj.colReason')}</TableHead>
                  <TableHead>{t('adj.colProject')}</TableHead>
                  <TableHead className="text-end">{t('adj.colValue')}</TableHead>
                  <TableHead>{t('adj.colStatus')}</TableHead>
                  <TableHead>{t('adj.colApproval')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(adjustments ?? []).map((row) => (
                  <TableRow
                    key={row.id}
                    className="cursor-pointer odd:bg-muted/30"
                    onClick={() => void navigate(`/inventory/adjustments/${row.id}`)}
                  >
                    <TableCell>
                      <Ltr value={row.code} className="font-mono text-sm" />
                    </TableCell>
                    <TableCell>
                      <DirectionChip direction={row.direction} />
                    </TableCell>
                    <TableCell>
                      <Ltr value={row.adjustmentDate} className="font-mono text-sm" />
                    </TableCell>
                    <TableCell className="text-sm">{row.warehouseName}</TableCell>
                    <TableCell className="text-sm">{t(`adj.reason.${row.reason}`)}</TableCell>
                    {/* PRJ-01: the project an issue went to, code first. */}
                    <TableCell className="text-sm">
                      {row.projectName !== null ? (
                        <>
                          <Ltr value={row.projectCode ?? ''} className="font-mono text-xs" />{' '}
                          {row.projectName}
                        </>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </TableCell>
                    <TableCell className="text-end">
                      <Amount value={row.totalValue} currency={baseCurrency} />
                    </TableCell>
                    <TableCell>
                      <VoucherStatusChip status={row.status} />
                    </TableCell>
                    <TableCell>
                      {row.requireApproval && row.approvalStatus ? (
                        <ApprovalChip status={row.approvalStatus} />
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}

          {/* NFR-42 — the range, the total, and the two steps. */}
          {!isPending && !isError && (data?.total ?? 0) > 0 ? (
            <Pager
              page={paging.page}
              pages={range.pages}
              first={range.first}
              last={range.last}
              total={range.total}
              onPage={paging.setPage}
              label={t('adj.title')}
            />
          ) : null}
        </CardContent>
      </Card>
    </div>
  );
}
